import { formatDispensingError } from './dispensingEntity';

/**
 * Build acknowledge body covering every line (server rejects partial lists).
 * rejected_quantity = requested − accepted (shortfall counts as rejected for status).
 * trigger_billing_reversal stays false — the reversal is driven from the client
 * against IPD billing (buildReversalPlan below), so letting the server reverse the
 * inventory bill as well would credit the patient twice.
 */
export function buildWardReturnAcknowledge(items, decisions, acknowledgedBy) {
  return {
    acknowledged_by: acknowledgedBy || null,
    trigger_billing_reversal: false,
    line_items: (items || []).map((it) => {
      const requested = Number(it.requested_quantity);
      const accepted = Number(decisions[it.id]?.accepted ?? 0);
      const rejected = Math.max(0, requested - accepted);
      const reason = decisions[it.id]?.reason;
      return {
        line_item_id: it.id,
        accepted_quantity: accepted,
        rejected_quantity: rejected,
        rejection_reason: rejected > 0 ? (reason?.trim() || null) : null,
      };
    }),
  };
}

/** Client-side mirror of server acknowledge rules. Returns error strings. */
export function validateWardReturnAcknowledge(items, req) {
  const errs = [];
  if (!items?.length) {
    errs.push('No line items to acknowledge. Refresh and try again.');
    return errs;
  }
  const sent = new Set((req.line_items || []).map((l) => l.line_item_id));
  for (const it of items) {
    if (!sent.has(it.id)) errs.push(`Missing decision for line ${it.id}`);
  }
  for (const line of req.line_items || []) {
    const it = items.find((i) => i.id === line.line_item_id);
    if (!it) {
      errs.push('Unknown line in acknowledge request. Refresh and try again.');
      continue;
    }
    const accepted = Number(line.accepted_quantity);
    const rejected = Number(line.rejected_quantity);
    const requested = Number(it.requested_quantity);
    if (Number.isNaN(accepted) || Number.isNaN(rejected) || accepted < 0 || rejected < 0) {
      errs.push('Accept and reject quantities must be zero or greater.');
      continue;
    }
    if (accepted + rejected > requested) {
      errs.push(`Accepted + rejected cannot exceed requested (${requested}) for a line.`);
    }
    if (accepted < requested && !(line.rejection_reason || '').trim()) {
      errs.push('Enter a reason when accepting less than the requested quantity.');
    }
    if ((line.rejection_reason || '').length > 500) {
      errs.push('Rejection reason cannot exceed 500 characters.');
    }
  }
  return [...new Set(errs)];
}

export function formatWardReturnError(err, fallback = 'Request failed') {
  const status = err?.status ?? err?.response?.status;
  const raw = formatDispensingError(err, fallback);
  const lower = String(raw || '').toLowerCase();

  if (status === 401) return 'Session expired — please log in again.';
  if (status === 404 || lower.includes('not found')) {
    return 'Return not found — refresh the list and try again.';
  }
  if (status === 409) {
    if (lower.includes('reject')) {
      return 'This return was already rejected. Refresh the list.';
    }
    return 'This return was already processed. Refresh the list.';
  }
  if (status === 502 || lower.includes('stock-ledger') || lower.includes('external service')) {
    return 'Stock service unavailable. You can retry acceptance.';
  }
  if (status === 422 || lower.includes('line item') || lower.includes('must be included')) {
    return raw || 'Invalid acknowledgement — check quantities and try again.';
  }
  return raw || fallback;
}

export function acknowledgeSuccessToast(status) {
  if (status === 'RETURNED') return 'Ward return accepted — stock credited';
  if (status === 'PARTIALLY_RETURNED') return 'Ward return partially accepted';
  if (status === 'REJECTED') return 'Ward return rejected — no stock moved';
  return 'Ward return acknowledged';
}

/** Retry acknowledge once on network/timeout/502. Never retry reject. */
export async function acknowledgeWardReturnWithRetry(fn) {
  try {
    return await fn();
  } catch (err) {
    const status = err?.status ?? err?.response?.status;
    const isNetwork = !status || status === 502 || err?.code === 'ECONNABORTED'
      || /network|timeout|unavailable/i.test(err?.message || '');
    if (!isNetwork) throw err;
    return fn();
  }
}

/** Inventory item id a pharmacy charge was tagged with, when the backend sets one. */
function chargeInventoryItemId(event) {
  const meta = event?.metadata || {};
  return meta.inventory_item_id || meta.item_id || null;
}

/**
 * Find the CHARGE event a returned line should be reversed against.
 * Returns null unless the match is unambiguous — zero or several candidates must
 * never be guessed at, or acknowledging a return could reverse an unrelated
 * service on the patient's bill. Charges already cancelled are skipped: a
 * CANCELLATION event points at its original via parent_event_id.
 */
export function resolveChargeEventForLine(line, chargeEvents) {
  const events = Array.isArray(chargeEvents) ? chargeEvents : [];
  const cancelled = new Set(
    events
      .filter((e) => String(e?.event_type).toUpperCase() === 'CANCELLATION')
      .map((e) => e?.parent_event_id || e?.metadata?.original_charge_event_id)
      .filter(Boolean),
  );
  const open = events.filter(
    (e) => String(e?.event_type).toUpperCase() === 'CHARGE' && !cancelled.has(e?.id),
  );

  // Strongest link: the inventory item id on the charge. `inventory_item_code`
  // from deriveInventoryScope can't be used here — a return line carries the
  // item UUID, not its code.
  if (line?.item_id) {
    const byItem = open.filter((e) => chargeInventoryItemId(e) === line.item_id);
    if (byItem.length === 1) return byItem[0];
    if (byItem.length > 1) return null;
  }

  // Next best: the dispensed batch, when the charge records it.
  if (line?.batch_number && line.batch_number !== '—') {
    const byBatch = open.filter((e) => e?.metadata?.batch_number === line.batch_number);
    if (byBatch.length === 1) return byBatch[0];
    if (byBatch.length > 1) return null;
  }

  // Last resort: unit rate, trusted only when exactly one charge carries it.
  const rate = Number(line?.unit_rate);
  if (!Number.isFinite(rate) || rate <= 0) return null;
  const byRate = open.filter((e) => Number(e?.unit_price) === rate);
  return byRate.length === 1 ? byRate[0] : null;
}

/**
 * Pair every accepted line with the billing call that reverses it.
 * CANCEL when the whole charged quantity came back; UPDATE to the retained
 * quantity otherwise — charges/{id}/cancel/ always reverses the charge in full,
 * so a partial return has to reduce the charge instead of cancelling it.
 * Lines with nothing accepted are skipped; lines with no confident charge match
 * come back in `unmatched` for the pharmacist to handle by hand.
 */
export function buildReversalPlan(items, decisions, chargeEvents) {
  const steps = [];
  const unmatched = [];
  (items || []).forEach((line) => {
    const accepted = Number(decisions?.[line.id]?.accepted ?? 0);
    if (!(accepted > 0)) return;
    const charge = resolveChargeEventForLine(line, chargeEvents);
    if (!charge) {
      unmatched.push(line);
      return;
    }
    const charged = Number(charge.quantity);
    const retained = Number.isFinite(charged) ? charged - accepted : 0;
    if (retained > 0) steps.push({ line, charge, action: 'UPDATE', quantity: retained });
    else steps.push({ line, charge, action: 'CANCEL' });
  });
  return { steps, unmatched };
}
